import { Typography } from '@persona-id/design-tokens';
import { useCallback } from 'react';
import { COLORS, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import Edit from 'images/icons/edit.svg';
import useToggle from 'lib/hooks/useToggle';

const CROSS_FADE_ANIMATION_DURATION = 0.1;
const BUTTON_EXPANSION_ANIMATION_DURATION = 0.2;
const BORDER_RADIUS = 3;
const BUTTON_HEIGHT = 48;
const INITIAL_BUTTON_WIDTH = 48;
const BUTTON_WIDTH = INITIAL_BUTTON_WIDTH * 2;

const ButtonContainer = styled('div')<{
  isDrawing: boolean;
}>`
  margin: ${SPACINGS.base * 1.5}px;
  display: flex;
  border-radius: ${BORDER_RADIUS}px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
  background-color: ${COLORS.white};
  width: ${(p) => (p.isDrawing ? `${BUTTON_WIDTH * 2}px` : `${INITIAL_BUTTON_WIDTH}px`)};
  transition-duration: ${BUTTON_EXPANSION_ANIMATION_DURATION}s;
`;

const AnimatedImageWrapper = styled('div')<{
  isDrawing: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  right: 12px;
  top: 12px;
  height: ${BUTTON_HEIGHT}px;
  width: ${(p) => (p.isDrawing ? `${BUTTON_WIDTH}px` : `${INITIAL_BUTTON_WIDTH}px`)};
  position: absolute;
  transition-duration: ${CROSS_FADE_ANIMATION_DURATION}s;
`;

const DrawButtonImageWrapper = styled(AnimatedImageWrapper)`
  opacity: ${(p) => (p.isDrawing ? 0 : 1)};
  transition-delay: ${(p) => (p.isDrawing ? '0s' : `${CROSS_FADE_ANIMATION_DURATION}s`)};
`;

const CancelButtonWrapper = styled(AnimatedImageWrapper)`
  opacity: ${(p) => (p.isDrawing ? 1 : 0)};
  transition-delay: ${(p) => (p.isDrawing ? '0s' : `${CROSS_FADE_ANIMATION_DURATION}s`)};
`;

// Ripped-off of Google Maps' Buttons
const ControlButton = styled('button')<{
  isDrawing: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${COLORS.white};
  cursor: pointer;
  user-select: none;
  height: ${BUTTON_HEIGHT}px;
  width: ${BUTTON_WIDTH}px;

  &:hover {
    background-color: ${(p) => (p.isDrawing ? COLORS.indigo10 : COLORS.white)};
  }
`;

const LeftControlButton = styled(ControlButton)`
  border-top-left-radius: ${BORDER_RADIUS}px;
  border-bottom-left-radius: ${BORDER_RADIUS}px;
  opacity: ${(p) => (p.isDrawing ? 1 : 0)};
  width: ${(p) => (p.isDrawing ? `${BUTTON_WIDTH}px` : '0px')};
`;

const RightControlButton = styled(ControlButton)`
  border-radius: ${BORDER_RADIUS}px;
`;

const ButtonSeparator = styled('div')<{
  isDrawing: boolean;
}>`
  position: absolute;
  left: 50%;
  background-color: ${COLORS.gray20};
  width: 2px;
  height: ${BUTTON_HEIGHT}px;
  visibility: ${(p) => (p.isDrawing ? 'visible' : 'hidden')};
`;

const DrawButtonText = styled('p')`
  ${Typography.TextMediumBold};
  color: ${COLORS.gray70};
  white-space: nowrap;
`;

const StyledEdit = styled(Edit)`
  path {
    stroke: ${COLORS.gray70};
  }
  &:hover {
    path {
      stroke: ${COLORS.black};
    }
  }
`;

interface DrawingControlProps {
  handleDrawingStart: () => void;
  handleDrawingComplete: () => void;
  handleDrawingCancel: () => void;
}

const DrawingControl = ({
  handleDrawingStart,
  handleDrawingComplete,
  handleDrawingCancel,
}: DrawingControlProps) => {
  const [isDrawing, { enable: enableDrawing, disable: disableDrawing }] = useToggle(false);

  const handleDrawingStartAndToggleDrawing = () => {
    enableDrawing();
    handleDrawingStart();
  };

  const handleDrawingCompleteAndToggleDrawing = () => {
    disableDrawing();
    handleDrawingComplete();
  };

  const handleDrawingCancelAndToggleDrawing = () => {
    disableDrawing();
    handleDrawingCancel();
  };

  const handleMultiButton = useCallback(() => {
    return isDrawing ? handleDrawingCancelAndToggleDrawing() : handleDrawingStartAndToggleDrawing();
  }, [isDrawing]);

  return (
    <ButtonContainer isDrawing={isDrawing}>
      <LeftControlButton isDrawing={isDrawing} onClick={handleDrawingCompleteAndToggleDrawing}>
        <DrawButtonText>Add item</DrawButtonText>
      </LeftControlButton>
      <ButtonSeparator isDrawing={isDrawing} />
      <RightControlButton isDrawing={isDrawing} onClick={handleMultiButton}>
        <DrawButtonImageWrapper isDrawing={isDrawing}>
          <StyledEdit />
        </DrawButtonImageWrapper>
        <CancelButtonWrapper isDrawing={isDrawing}>
          <DrawButtonText>Cancel</DrawButtonText>
        </CancelButtonWrapper>
      </RightControlButton>
    </ButtonContainer>
  );
};

export default DrawingControl;
